import {
  differenceInCalendarMonths,
  differenceInYears,
  getMonth,
  getQuarter,
  isSameDay,
  isSameQuarter,
} from 'date-fns';
import { WithOptionalStartAndEndDate } from '../data-point-pickers/types';
import { DateRange, DateRangeAndYear } from './types';

export function convertDateToUTC(date: Date) {
  // From https://stackoverflow.com/a/6777470/9252547
  return new Date(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
    date.getUTCHours(),
    date.getUTCMinutes(),
    date.getUTCSeconds(),
    date.getUTCMilliseconds(),
  );
}

function getDateWithoutTimestamp(date: Date) {
  // Returns a date object with timestamp of 00:00:00.000Z
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();
  return new Date(Date.UTC(year, month, day));
}

export function toDateRangeAndYear(datum: WithOptionalStartAndEndDate): DateRangeAndYear | undefined {
  if (!datum.endDate || !datum.startDate) {
    return undefined;
  }

  const startDate = getDateWithoutTimestamp(new Date(datum.startDate));
  const endDate = getDateWithoutTimestamp(new Date(datum.endDate));

  if (startDate > endDate) {
    throw Error(
      `Start date cannot be after the end date: (start: ${startDate.toString()}, end: ${endDate.toString()})`,
    );
  }

  if (differenceInYears(startDate, endDate) > 0) {
    throw Error(`Intervals that span over one year currently not supported.`);
  }

  if (isSameDay(startDate, endDate)) {
    // TODO: Remove this legacy branch
    return {
      dateRange: DateRange.YEAR,
      year: startDate.getFullYear(),
    };
  }

  if (differenceInCalendarMonths(endDate, startDate) <= 1) {
    const month = getMonth(startDate) + 1;
    const monthRange = (() => {
      switch (month) {
        case 1:
          return DateRange.M1;
        case 2:
          return DateRange.M2;
        case 3:
          return DateRange.M3;
        case 4:
          return DateRange.M4;
        case 5:
          return DateRange.M5;
        case 6:
          return DateRange.M6;
        case 7:
          return DateRange.M7;
        case 8:
          return DateRange.M8;
        case 9:
          return DateRange.M9;
        case 10:
          return DateRange.M10;
        case 11:
          return DateRange.M11;
        case 12:
          return DateRange.M12;
        default:
          throw Error(`Incompatible month ${month}`);
      }
    })();
    return {
      dateRange: monthRange,
      year: startDate.getFullYear(),
    };
  }

  /* RN: we need to subtract 24 hours from the endDate for isSameQuarter() to work for e.g. posting portfolio constituents.
     isSameQuarter() seems to work on a crude level, e.g. dates 2022-07-01T00:00:00.000Z and 2022-09-30T23:59:59.999Z wouldn't be in the same quarter.

     EDIT: Something wonky going on here, as elsewhere the isSameQuarter() function seems to work correctly. Fixing soon.
   */
  if (isSameQuarter(startDate, new Date(endDate.getTime() - 1000 * 60 * 60 * 24))) {
    const quarter = getQuarter(startDate);
    const quarterRange = (() => {
      switch (quarter) {
        case 1:
          return DateRange.Q1;
        case 2:
          return DateRange.Q2;
        case 3:
          return DateRange.Q3;
        case 4:
          return DateRange.Q4;
        default:
          throw Error(`Incompatible quarter ${quarter}`);
      }
    })();
    return { dateRange: quarterRange, year: startDate.getFullYear() };
  }

  return { dateRange: DateRange.YEAR, year: startDate.getFullYear() };
}

export function getThisYearRange(): DateRangeAndYear {
  return {
    year: new Date().getFullYear(),
    dateRange: DateRange.YEAR,
  };
}

export function getThisYearStartAndEnd() {
  return toStartAndEndDate(getThisYearRange());
}

export function toStartAndEndDate(dateRange: DateRangeAndYear) {
  const startDate = toStartDate(dateRange);
  const endDate = toEndDate(dateRange);
  return { startDate, endDate };
}

function toStartDate(dateRange: DateRangeAndYear) {
  switch (dateRange.dateRange) {
    case DateRange.YEAR:
    case DateRange.M1:
    case DateRange.Q1:
      return new Date(`${dateRange.year}-01`);
    case DateRange.M2:
      return new Date(`${dateRange.year}-02`);
    case DateRange.M3:
      return new Date(`${dateRange.year}-03`);
    case DateRange.M4:
    case DateRange.Q2:
      return new Date(`${dateRange.year}-04`);
    case DateRange.M5:
      return new Date(`${dateRange.year}-05`);
    case DateRange.M6:
      return new Date(`${dateRange.year}-06`);
    case DateRange.M7:
    case DateRange.Q3:
      return new Date(`${dateRange.year}-07`);
    case DateRange.M8:
      return new Date(`${dateRange.year}-08`);
    case DateRange.M9:
      return new Date(`${dateRange.year}-09`);
    case DateRange.M10:
    case DateRange.Q4:
      return new Date(`${dateRange.year}-10`);
    case DateRange.M11:
      return new Date(`${dateRange.year}-11`);
    case DateRange.M12:
      return new Date(`${dateRange.year}-12`);
    default:
      throw new Error(`Unexpected`);
  }
}

function toEndDate(dateRange: DateRangeAndYear) {
  // date-fns endOfQuarter endOfYear fuck up if the machine timezone is not UTC
  // we should really move to moment
  switch (dateRange.dateRange) {
    case DateRange.M1:
      return new Date(`${dateRange.year}-01-31T23:59:59.999Z`);
    case DateRange.M2:
      return new Date(`${dateRange.year}-02-28T23:59:59.999Z`);
    case DateRange.M3:
    case DateRange.Q1:
      return new Date(`${dateRange.year}-03-31T23:59:59.999Z`);
    case DateRange.M4:
      return new Date(`${dateRange.year}-04-30T23:59:59.999Z`);
    case DateRange.M5:
      return new Date(`${dateRange.year}-05-31T23:59:59.999Z`);
    case DateRange.M6:
    case DateRange.Q2:
      return new Date(`${dateRange.year}-06-30T23:59:59.999Z`);
    case DateRange.M7:
      return new Date(`${dateRange.year}-07-31T23:59:59.999Z`);
    case DateRange.M8:
      return new Date(`${dateRange.year}-08-31T23:59:59.999Z`);
    case DateRange.M9:
    case DateRange.Q3:
      return new Date(`${dateRange.year}-09-30T23:59:59.999Z`);
    case DateRange.M10:
      return new Date(`${dateRange.year}-10-31T23:59:59.999Z`);
    case DateRange.M11:
      return new Date(`${dateRange.year}-11-30T23:59:59.999Z`);
    case DateRange.M12:
    case DateRange.Q4:
    case DateRange.YEAR:
      return new Date(`${dateRange.year}-12-31T23:59:59.999Z`);
    default:
      throw new Error(`Unexpected`);
  }
}

export function dateRangeOverlapsWithStartAndEndDate(
  range: DateRangeAndYear,
  resultsToCompareTo: WithOptionalStartAndEndDate[],
  thisResult?: WithOptionalStartAndEndDate,
) {
  const datesOfThisResult = thisResult && toDateRangeAndYear(thisResult);
  if (range.dateRange === datesOfThisResult?.dateRange && range.year === datesOfThisResult.year) {
    return false;
  }

  for (const result of resultsToCompareTo) {
    const resultDates = toDateRangeAndYear(result);

    if (resultDates && resultDates.year === range.year) {
      if (resultDates.dateRange === DateRange.YEAR || range.dateRange === DateRange.YEAR) {
        return true;
      }
      if (range.dateRange === resultDates.dateRange) {
        return true;
      }
    }
  }

  return false;
}

export function stringToDateRange(yearOrMonthOrQuarter: string | undefined) {
  switch (yearOrMonthOrQuarter) {
    case 'M1':
      return DateRange.M1;
    case 'M2':
      return DateRange.M2;
    case 'M3':
      return DateRange.M3;
    case 'M4':
      return DateRange.M4;
    case 'M5':
      return DateRange.M5;
    case 'M6':
      return DateRange.M6;
    case 'M7':
      return DateRange.M7;
    case 'M8':
      return DateRange.M8;
    case 'M9':
      return DateRange.M9;
    case 'M10':
      return DateRange.M10;
    case 'M11':
      return DateRange.M11;
    case 'M12':
      return DateRange.M12;
    case 'Q1':
      return DateRange.Q1;
    case 'Q2':
      return DateRange.Q2;
    case 'Q3':
      return DateRange.Q3;
    case 'Q4':
      return DateRange.Q4;
    case undefined:
      throw Error('Error: no input to convert into date range.');
    default:
      return DateRange.YEAR;
  }
}

export function solveYearFromStartDateAndEndDate(startDate: string, endDate: string) {
  const [l, r] = [new Date(startDate), new Date(endDate)];
  const middle = new Date((l.getTime() + r.getTime()) / 2);
  return middle.getFullYear();
}
